// routes for redux-first-router

import { NOT_FOUND } from 'redux-first-router'
import { useSelector } from 'react-redux'

export const SLICE = 'location'

// fixed interview steps
export const INTERVIEW_STEPS = {
  INTRODUCTION: 'introduction',
  REPORT: 'report',
  SUMMARY: 'summary',
}

// route actions
export { NOT_FOUND }
export const DASHBOARD = 'router/DASHBOARD'
export const INTERVIEW = 'router/INTERVIEW'
export const PANEL = 'router/PANEL'
export const MODEL = 'router/MODEL'
export const PAGE = 'router/PAGE'
export const QUIZ_MAIN = 'router/QUIZ_MAIN'
export const QUIZ_TERMS = 'router/QUIZ_TERMS'
export const PERSONALITY_QUIZ = 'router/PERSONALITY_QUIZ'
export const INTEREST_QUIZ = 'router/INTEREST_QUIZ'
export const GMA_QUIZ_WELCOME = 'router/GMA_QUIZ_WELCOME'
export const GMA_QUIZ_GUIDE = 'router/GMA_QUIZ_GUIDE'
export const GMA_QUIZ = 'router/GMA_QUIZ'
export const CANDIDATE_REPORT = 'router/CANDIDATE_REPORT'
export const EXTERNAL_CANDIDATE_REPORT = 'router/EXTERNAL_CANDIDATE_REPORT'
export const INTERVIEW_TEMPLATE = 'router/INTERVIEW_TEMPLATE'
export const LOGIN = 'router/LOGIN'
export const PASSWORD_RESET = 'router/PASSWORD_RESET'
export const PASSWORD_CHANGE = 'router/PASSWORD_CHANGE'
export const PASSWORD_CHANGE_DONE = 'router/PASSWORD_CHANGE_DONE'
export const REGISTER_ACCOUNT = 'router/REGISTER_ACCOUNT'
export const PROJECTS = 'router/PROJECTS'
export const TEMPLATES = 'router/TEMPLATES'
export const TEMPLATES_CREATOR = 'router/TEMPLATES_CREATOR'
export const CAREER = 'router/CAREER'
export const DEPARTMENTS = 'router/DEPARTMENTS'

// redux-first-router routes
export const routesMap = {
  [MODEL]: '/model/:model/:pk?/:tab?/',
  [INTERVIEW]: '/interview/candidates/:uuid/',
  [PAGE]: '/page/:page/:tab?/',
  [QUIZ_MAIN]: '/respondent/:uuid/',
  [QUIZ_TERMS]: '/respondent/:uuid/terms/',
  [GMA_QUIZ_WELCOME]: '/respondent/:uuid/gma/welcome/',
  [GMA_QUIZ_GUIDE]: '/respondent/:uuid/gma/guide/',
  [GMA_QUIZ]: '/respondent/:uuid/gma/questions/:page?/',
  [PERSONALITY_QUIZ]: '/respondent/:uuid/personality/:page?/',
  [INTEREST_QUIZ]: '/respondent/:uuid/interest/:page?/',
  [CANDIDATE_REPORT]: '/report/:uuid/:language/',
  [EXTERNAL_CANDIDATE_REPORT]: '/report/:uuid/:language/candidate',
  [INTERVIEW_TEMPLATE]: '/interview/template/:uuid/',
  [NOT_FOUND]: '/404/',
  [LOGIN]: '/auth/login/',
  [REGISTER_ACCOUNT]: '/auth/register/:done?/',
  [PASSWORD_RESET]: '/auth/password/reset/:done?/',
  [PASSWORD_CHANGE]: '/auth/password/change/:uidb64/:token/', // same as in django backend
  [PASSWORD_CHANGE_DONE]: '/auth/password/change/done/',
  [PROJECTS]: '/projects/:id?/:tab?',
  [TEMPLATES]: '/templates/:tab/:id?',
  [DEPARTMENTS]: '/departments/:id?',
  [TEMPLATES_CREATOR]: '/templates/:tab/:uuid/creator',
  [CAREER]: '/career/:tab/:id?',
  [PANEL]: '/panel/:pk?/:id?',
  [DASHBOARD]: '/',
}

// quiz paging actions
// Used in saga to calculate final routing action
export const NEXT_PAGE = 'router/NEXT_PAGE'
export const PREVIOUS_PAGE = 'router/PREVIOUS_PAGE'
export const NEXT_UNANSWERED = 'router/NEXT_UNANSWERED'
export const nextPage = () => ({ type: NEXT_PAGE })
export const previousPage = () => ({ type: PREVIOUS_PAGE })
export const nextUnanswered = () => ({ type: NEXT_UNANSWERED })

// route action creators
export const toModel = (model, pk, tab?, query = {}) => ({
  type: MODEL,
  payload: { model, pk, tab },
  query,
})
export const toProjects = (id, tab?, query = {}) => ({
  type: PROJECTS,
  payload: { id, tab },
  query,
})
export const toTemplates = (tab, id?, query = {}) => ({
  type: TEMPLATES,
  payload: { id, tab },
  query,
})
export const toCareer = (tab, id, query = {}) => ({
  type: CAREER,
  payload: { id, tab },
  query,
})
export const toDepartments = (id, query = {}) => ({
  type: DEPARTMENTS,
  payload: { id },
  query,
})
export const toUserDepartments = ({
  pk,
  id,
  query = {},
}: {
  pk: unknown
  id?: unknown
  query?: object
}) => ({
  type: PANEL,
  payload: { pk, id },
  query,
})
export const toTemplatesCreator = ({ tab, uuid }) => ({
  type: TEMPLATES_CREATOR,
  payload: { tab, uuid },
})
export const toPanel = (pk, query = {}) => ({
  type: PANEL,
  payload: { pk },
  query,
})
export const toPage = (page, tab?) => ({ type: PAGE, payload: { page, tab } })

export const toQuiz = ({ uuid }) => ({ type: QUIZ_MAIN, payload: { uuid } })
export const toTerms = ({ uuid }) => ({ type: QUIZ_TERMS, payload: { uuid } })

type QuizRoute = { uuid?: string; page?: number; reversed?: boolean }

export const toGmaQuiz = ({ uuid, page = 0, reversed }: QuizRoute = {}) => ({
  type: GMA_QUIZ,
  payload: { uuid, page, reversed },
})
export const toGmaQuizWelcome = ({ uuid }) => ({
  type: GMA_QUIZ_WELCOME,
  payload: { uuid },
})
export const toGmaQuizGuide = ({ uuid }) => ({
  type: GMA_QUIZ_GUIDE,
  payload: { uuid },
})
export const toPersonalityQuiz = ({
  uuid,
  page = 0,
  reversed,
}: QuizRoute = {}) => ({
  type: PERSONALITY_QUIZ,
  payload: { uuid, page, reversed },
})
export const toInterestQuiz = ({ uuid, page = 0, reversed = false }) => ({
  type: INTEREST_QUIZ,
  payload: { uuid, page, reversed },
})
export const toReport = ({ uuid, language = 'en' }) => ({
  type: CANDIDATE_REPORT,
  payload: { uuid, language },
})
export const toLogin = () => ({ type: LOGIN, payload: {} })
export const toRegister = done => ({
  type: REGISTER_ACCOUNT,
  payload: { done },
})
export const toPasswordReset = (done?: string) => ({
  type: PASSWORD_RESET,
  payload: { done },
})
export const toPasswordChange = ({ token, uid }) => ({
  type: PASSWORD_CHANGE,
  payload: { token, uidb64: uid },
})
export const toPasswordChangeDone = () => ({
  type: PASSWORD_CHANGE_DONE,
  payload: {},
})
export const toInterview = ({
  meta,
  uuid,
}: {
  meta?: unknown
  uuid?: string
}) => ({
  type: INTERVIEW,
  payload: { uuid },
  meta,
})

// selectors
export const getLocation = state => state[SLICE] || {}
export const getQuizPage = state => {
  const {
    type,
    payload: { page, uuid, reversed = false },
  } = getLocation(state)
  const parsedPage = parseInt(page, 10)

  return {
    type,
    uuid,
    page: Number.isFinite(parsedPage) ? parsedPage : 0,
    reversed,
  }
}

// hooks
export const useLocation = () => {
  return useSelector(getLocation)
}
